import { z } from 'zod';
import { toDate, startOfDay, addYears, addDays } from 'date-fns';
import {
  LogicLogicReadModelType,
  type LogicWorkspaceWorkspaceReadModel
} from '@r/api/generated/client.schemas.ts';

const renewalStepSchema = z.object({
  name: z.string().max(50),
  description: z.string().nullable().optional(),
  stepPoint: z.number().min(0)
});

export const POINT_EXPIRATION_RELATIVE_PATTERN =
  /^last day of december this year(?:\s*\+\s*(\d+)\s*years?)?(?:\s*,\s*23:59:59)?$/i;

/** Date d’expiration fixe proposée par défaut : 31 décembre de l’année suivante. */
export function getDefaultPointExpirationDate(today: Date = new Date()): Date {
  return new Date(today.getFullYear() + 1, 11, 31);
}

/**
 * Dérive le type d’expiration et les champs formulaire depuis la valeur stockée côté API.
 */
export function parsePointExpirationDefaults(rawValue?: string | null): {
  type: 'fixed' | 'yearEnd';
  date: Date;
  yearOffset: number;
} {
  const date = getDefaultPointExpirationDate();
  const match = rawValue ? POINT_EXPIRATION_RELATIVE_PATTERN.exec(rawValue.trim()) : null;
  if (match) {
    return { type: 'yearEnd', date, yearOffset: Number(match[1] ?? 0) };
  }
  return { type: 'fixed', date, yearOffset: 0 };
}

export type RenewalProgramFormSchema = {
  startAt: Date;
  endAt: Date;
  pointExpirationType: 'fixed' | 'yearEnd';
  pointExpirationDate?: Date;
  pointExpirationYearOffset?: number;
  steps?: z.infer<typeof renewalStepSchema>[];
};

/** Schéma Zod du renouvellement (dates, expiration points, paliers si logique STEP). */
export function buildRenewalFormSchema(
  t: (key: string) => string,
  currentLogicEndAt?: string | null,
  isStepProgram = false
) {
  const currentEndDate = currentLogicEndAt ? startOfDay(toDate(currentLogicEndAt)) : null;

  return z
    .object({
      startAt: z.date(),
      endAt: z.date(),
      pointExpirationType: z.enum(['fixed', 'yearEnd']),
      pointExpirationDate: z.date().optional(),
      pointExpirationYearOffset: z.number().min(0).max(10).optional(),
      steps: z.array(renewalStepSchema).optional()
    })
    .superRefine((data, ctx) => {
      if (isStepProgram && (data.steps?.length ?? 0) === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.workspace.logic.step.required'),
          path: ['steps']
        });
      }
      if (data.pointExpirationType === 'fixed' && data.pointExpirationDate && data.startAt) {
        const expDay = startOfDay(data.pointExpirationDate);
        const startDay = startOfDay(data.startAt);
        if (expDay.getTime() < startDay.getTime()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('form.workspace.point.expiration.validation.after.start'),
            path: ['pointExpirationDate']
          });
        }
      }
      // La nouvelle logique doit commencer strictement après la fin de l'actuelle (jour suivant),
      // le backend considère la logique active sur toute la journée de fin.
      if (
        currentEndDate &&
        data.startAt &&
        startOfDay(data.startAt).getTime() <= currentEndDate.getTime()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.workspace.renewal.start.after.current.end'),
          path: ['startAt']
        });
      }
      if (data.startAt && data.endAt && data.startAt >= data.endAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.workspace.operation.date.start.after.date.end'),
          path: ['endAt']
        });
      }
      if (data.pointExpirationType === 'fixed' && !data.pointExpirationDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.workspace.point.expiration.validation.date.required'),
          path: ['pointExpirationDate']
        });
      }
      if (data.pointExpirationType === 'yearEnd' && data.pointExpirationYearOffset === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('form.workspace.point.expiration.validation.year.required'),
          path: ['pointExpirationYearOffset']
        });
      }
    });
}

/**
 * Valeurs par défaut pour le formulaire de renouvellement
 * La date de début du nouveau programme = lendemain de la fin de la logique actuelle,
 * car le backend considère une logique "active" sur toute la journée de fin (pas de chevauchement).
 * La date de fin par défaut = 1 an après cette date de début.
 */
export function getRenewalFormDefaultValues(
  currentLogic: LogicWorkspaceWorkspaceReadModel | null | undefined
): RenewalProgramFormSchema {
  const pointExpirationDefaults = parsePointExpirationDefaults(currentLogic?.pointExpirationDate);
  const today = startOfDay(new Date());
  const currentEndAt = currentLogic?.endAt ? startOfDay(toDate(currentLogic.endAt)) : null;
  const firstAllowedStart = currentEndAt ? addDays(currentEndAt, 1) : today;
  const startAt =
    currentEndAt && firstAllowedStart.getTime() >= today.getTime() ? firstAllowedStart : today;
  const endAt = addYears(startAt, 1);
  const isStepProgram = currentLogic?.logic?.type === LogicLogicReadModelType.STEP;

  return {
    startAt,
    endAt,
    pointExpirationType: pointExpirationDefaults.type,
    pointExpirationDate: pointExpirationDefaults.date,
    pointExpirationYearOffset: pointExpirationDefaults.yearOffset,
    ...(isStepProgram
      ? {
          steps: (currentLogic?.steps ?? []).map((step) => ({
            name: step.name,
            stepPoint: step.stepPoint,
            description: step.description ?? null
          }))
        }
      : {})
  };
}
